// src/pages/PerfilesOnboarding.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Fade
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';

// Importar formularios específicos
import FormularioEconomiaColaborativa from '../components/perfiles/FormularioEconomiaColaborativa';
import FormularioSimpatizante from '../components/perfiles/FormularioSimpatizante';

const perfilesConfig = [
  {
    id: 'economia-colaborativa',
    emoji: '🤝',
    color: '#1E88E5',
    titulo: 'Economías Colaborativas',
    subtitulo: 'Únete a cooperativas y redes de apoyo mutuo',
    descripcion: 'Accede a proyectos cooperativos, redes de comercio justo, y oportunidades de trabajo colaborativo.',
    beneficios: [
      'Conexión con cooperativas de tu sector',
      'Proyectos de economía solidaria',
      'Capacitación en gestión cooperativa',
      'Red de comercio justo y trueque'
    ],
    tiempo: '5 min',
    componente: FormularioEconomiaColaborativa
  },
  {
    id: 'simpatizante',
    emoji: '❤️',
    color: '#E63946',
    titulo: 'Solo Simpatizante',
    subtitulo: 'Apoya el movimiento sin compromisos',
    descripcion: 'Mantente informado, participa en eventos públicos y ejerce tu derecho al voto.',
    beneficios: [
      'Boletín informativo mensual',
      'Invitaciones a eventos públicos',
      'Sin cuotas ni obligaciones',
      'Derecho a voto por candidatos'
    ],
    tiempo: '2 min',
    componente: FormularioSimpatizante
  },
  {
    id: 'vendedor',
    emoji: '🛒',
    color: '#FF9800',
    titulo: 'Vender mis Productos',
    subtitulo: 'Accede a mercados y ferias solidarias',
    descripcion: 'Te conectamos con ferias, mercados campesinos, tiendas solidarias y plataformas de comercio.',
    beneficios: [
      'Acceso a ferias y mercados locales',
      'Vitrina en plataforma digital',
      'Capacitación en ventas',
      'Red de distribución solidaria'
    ],
    tiempo: '4 min',
    componente: FormularioSimpatizante // Temporal, crear después
  },
  {
    id: 'emprendedor',
    emoji: '💼',
    color: '#9C27B0',
    titulo: 'Crear mi Negocio',
    subtitulo: 'Apoyo para tu emprendimiento',
    descripcion: 'Asesoría, microcréditos solidarios, formación empresarial y conexión con mentores.',
    beneficios: [
      'Asesoría personalizada gratuita',
      'Acceso a microcréditos sin interés',
      'Formación en gestión empresarial',
      'Mentoría con empresarios del movimiento'
    ],
    tiempo: '6 min',
    componente: FormularioSimpatizante // Temporal, crear después
  }
];

const PerfilesOnboarding = () => {
  const navigate = useNavigate();
  const [perfilSeleccionado, setPerfilSeleccionado] = useState(null);

  if (perfilSeleccionado) {
    const FormularioComponente = perfilSeleccionado.componente;
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => setPerfilSeleccionado(null)}
            sx={{
              color: '#1E88E5',
              fontWeight: 600,
              '&:hover': { backgroundColor: 'rgba(30, 136, 229, 0.08)' }
            }}
          >
            Ver todas las opciones
          </Button>
        </Box>
        <FormularioComponente 
          perfil={perfilSeleccionado} 
          onVolver={() => setPerfilSeleccionado(null)} 
        />
      </Container>
    );
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, rgba(30, 136, 229, 0.05) 0%, rgba(76, 175, 80, 0.05) 100%)',
      py: 6
    }}>
      <Container maxWidth="xl">
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h3" sx={{
            fontWeight: 800,
            color: '#1E88E5',
            mb: 2,
            fontSize: { xs: '2rem', md: '3rem' }
          }}>
            ¿Cómo quieres participar en Alianza Social?
          </Typography>
          <Typography variant="h6" sx={{
            color: '#757575',
            maxWidth: '800px',
            mx: 'auto',
            lineHeight: 1.6
          }}>
            Selecciona la opción que mejor se ajuste a tus intereses. Cada perfil tiene beneficios específicos.
          </Typography>
        </Box>

        <Box sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(4, 1fr)'
          },
          gap: 3
        }}>
          {perfilesConfig.map((perfil, index) => (
            <Fade in={true} timeout={300 + index * 100} key={perfil.id}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: '20px',
                  border: `2px solid ${perfil.color}30`,
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: `0 20px 40px ${perfil.color}40`,
                    borderColor: perfil.color
                  }
                }}
                onClick={() => setPerfilSeleccionado(perfil)}
              >
                <Box sx={{
                  position: 'absolute',
                  top: -30,
                  right: -30,
                  fontSize: '120px',
                  opacity: 0.06,
                  transform: 'rotate(-15deg)'
                }}>
                  {perfil.emoji}
                </Box>

                <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{
                      width: 64,
                      height: 64,
                      borderRadius: '16px',
                      background: `linear-gradient(135deg, ${perfil.color}15, ${perfil.color}30)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '32px'
                    }}>
                      {perfil.emoji}
                    </Box>
                    <Chip
                      label={perfil.tiempo}
                      size="small"
                      sx={{
                        backgroundColor: `${perfil.color}15`,
                        color: perfil.color,
                        fontWeight: 700
                      }}
                    />
                  </Box>

                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {perfil.titulo}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#757575', mb: 2, fontSize: '0.85rem' }}>
                    {perfil.subtitulo}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#616161', mb: 2, lineHeight: 1.5 }}>
                    {perfil.descripcion}
                  </Typography>

                  <Box sx={{ mt: 'auto' }}>
                    <Typography variant="caption" sx={{
                      fontWeight: 700,
                      color: perfil.color,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'block',
                      mb: 1
                    }}>
                      Beneficios destacados:
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                      {perfil.beneficios.slice(0, 2).map((beneficio, idx) => (
                        <Typography
                          component="li"
                          key={idx}
                          variant="caption"
                          sx={{ color: '#616161', lineHeight: 1.6, mb: 0.3 }}
                        >
                          {beneficio}
                        </Typography>
                      ))}
                    </Box>
                    <Typography variant="caption" sx={{ color: perfil.color, fontWeight: 600, mt: 1, display: 'block' }}>
                      + {perfil.beneficios.length - 2} beneficios más
                    </Typography>
                  </Box>

                  <Button
                    fullWidth
                    variant="contained"
                    sx={{
                      mt: 2,
                      backgroundColor: perfil.color,
                      borderRadius: '12px',
                      fontWeight: 700,
                      '&:hover': {
                        backgroundColor: perfil.color,
                        filter: 'brightness(0.9)'
                      }
                    }}
                  >
                    Comenzar
                  </Button>
                </CardContent>
              </Card>
            </Fade>
          ))}
        </Box>

        <Box sx={{
          mt: 6,
          p: 3,
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          borderRadius: '16px',
          border: '2px solid rgba(30, 136, 229, 0.2)',
          textAlign: 'center'
        }}>
          <Typography variant="body2" sx={{ color: '#424242', mb: 1 }}>
            💡 <strong>Nota:</strong> Puedes seleccionar múltiples perfiles si te interesan varias áreas.
          </Typography>
          <Typography variant="caption" sx={{ color: '#757575' }}>
            🔒 Toda tu información está protegida bajo nuestra política de privacidad.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default PerfilesOnboarding;